import {PlayerColor} from '@colonist/game-states'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'

export class UIAPIEndpoints {
    static socketServerWSS(): string {
        return UIUtilsEjsVariables.ejsSocketServerWss
    }

    static googleAuth(callbackHash?: string): string {
        if(callbackHash) return `auth/google?callbackHash=${callbackHash}`
        return '/auth/google'
    }

    static appleAuth(callbackHash?: string): string {
        if(callbackHash) return `/auth/apple?callbackHash=${callbackHash}`
        return '/auth/apple'
    }

    static discordAuth(callbackHash?: string): string {
        if(callbackHash) return `/auth/discord?callbackHash=${callbackHash}`
        return '/auth/discord'
    }

    static discordActivityAuth(): string {
        return '/auth/discord-activity'
    }

    static googleLink(callbackHash?: string): string {
        if(callbackHash) return `/auth-link/google?callbackHash=${callbackHash}`
        return '/auth-link/google'
    }

    static appleLink(callbackHash?: string): string {
        if(callbackHash) return `/auth-link/apple?callbackHash=${callbackHash}`
        return '/auth-link/apple'
    }

    static discordLink(callbackHash?: string): string {
        if(callbackHash) return `/auth-link/discord?callbackHash=${callbackHash}`
        return '/auth-link/discord'
    }

    static googleUnlink(): string {
        return '/auth-unlink/google'
    }

    static appleUnlink(): string {
        return '/auth-unlink/apple'
    }

    static discordUnlink(): string {
        return '/auth-unlink/discord'
    }

    static logout(): string {
        return '/logout'
    }

    static checkoutProductUrl(productType: any): string {
        return `/checkout?product=${productType}`
    }

    static checkoutMembershipUrl(membershipType: any): string {
        return `/checkout?membership=${membershipType}`
    }

    static checkoutOneTimePurchaseUrl(oneTimePurchaseType: any): string {
        return `/checkout?one-time-purchase=${oneTimePurchaseType}`
    }

    static apiPayGetStoreStripeSession(): string {
        return '/api/pay/get-store-stripe-session'
    }

    static apiPayGetStoreXsollaSession(): string {
        return '/api/pay/get-store-xsolla-session'
    }

    static apiPayCreateCustomerPortalSession(): string {
        return '/api/pay/stripe-create-customer-portal-session'
    }

    static apiPayPurchaseItem(): string {
        return '/api/pay/purchase-item'
    }

    static apiActivateGiftMembership(): string {
        return '/api/membership/activate-gift'
    }

    static apiCancelGiftMembership(): string {
        return '/api/membership/cancel-gift'
    }

    static apiAnalytics(): string {
        return '/api/analytic-events'
    }

    static apiUpdateProfile(): string {
        return '/api/profile-edit'
    }

    static apiDeleteProfile(): string {
        return '/api/profile/delete'
    }

    static apiFindGameState(): string {
        return '/api/find-game-ranked-state'
    }

    static apiUserState(): string {
        return '/api/user-state'
    }

    static apiUserStateCreate(): string {
        return '/api/user-state/create'
    }

    static apiUserLoginState(): string {
        return '/api/user-login-state'
    }

    static apiUserProfileAddFriend(): string {
        return '/api/profile-edit/add-friend-request'
    }
    static apiUserProfileAcceptFriend(): string {
        return '/api/profile-edit/accept-friend-request'
    }
    static apiUserProfileRemoveFriend(): string {
        return '/api/profile-edit/remove-friend'
    }

    static apiUserProfileRankedSeasons(): string {
        return '/api/profile/ranked-seasons'
    }

    static apiGetDiscordAccessToken(): string {
        return '/api/discord/token'
    }

    static apiHandleDiscordPurchase(): string {
        return '/api/discord/handle-purchase'
    }

    static apiSaveDiscordData(): string {
        return '/api/discord/save-data'
    }

    static apiFriendRequestsReceived(): string {
        return `/api/header/friend-requests-received`
    }

    static apiSetUserLanguage(): string {
        return `/api/user/language`
    }

    static apiSetUserReferrer(): string {
        return `/api/user/set-referrer`
    }

    static apiGetUserReferralCount(): string {
        return `/api/user/get-referral-count`
    }

    static apiAssignABTest(): string {
        return `/api/user/assign-ab-test`
    }

    static apiGetRoomList(): string {
        return `/api/room-list.json`
    }

    static apiGetGameList(): string {
        return `/api/game-list.json`
    }

    static apiGetRegions(): string {
        return `/api/regions.json`
    }

    static readonly apiUserProfile = Object.freeze({
        stickyPlayerInfo: (username: string): string => `/api/profile/${encodeURIComponent(username)}/sticky-player-info`,
        overview: (username: string): string => `/api/profile/${encodeURIComponent(username)}/overview`,
        history: (username: string): string => `/api/profile/${encodeURIComponent(username)}/history`,
        items: (username: string): string => `/api/profile/${encodeURIComponent(username)}/items`,
        friends: (): string => `/api/profile/friends`,
        ranked: (username: string): string => `/api/profile/${encodeURIComponent(username)}/ranked`,
        username: (userId: string): string => `/api/profile/${userId}/username`,
    })

    static readonly apiLeaderboards = Object.freeze({
        data: (rankedModeType: string, leaderboardUrl: string): string => `/api/leaderboards/${rankedModeType}/${leaderboardUrl}`,
        usernameSearch: (rankedModeType: string, leaderboardUrl: string): string => `/api/leaderboards-username-search/${rankedModeType}/${leaderboardUrl}`,
        tabs: (leaderboardUrl: string): string => `/api/leaderboards-tabs/${leaderboardUrl}`,
        stickyRowData: (rankedModeType: string, leaderboardUrl: string): string => `/api/leaderboards-my-sticky-row-data/${rankedModeType}/${leaderboardUrl}`,

    })

    static apiReportPlayer(username: string): string {
        return `/api/${encodeURIComponent(username)}/report-user`
    }

    static apiFeedbackFormSubmit() {
        return `/api/feedback-form`
    }

    static apiFeedbackFormShow(): string {
        return `/api/show-feedback-form`
    }

    static apiGetNotifications(): string {
        return `/api/notifications`
    }

    static apiMarkNotificationAsRead(notificationId: string): string {
        return `/api/notifications/${notificationId}/mark-as-read`
    }

    static apiValidateUsername(username: string): string {
        return `/api/validate-username/${username}`
    }

    static apiChangeRegion(): string {
        return `/api/profile/change-region`
    }

    static apiReplayShareableLink(gameId: string, playerColor: string): string {
        return `/api/replay/shareable-link?gameId=${gameId}&playerColor=${playerColor}`
    }

    static apiReplayDataFromSlug(replayUrlSlug: string): string {
        return `/api/replay/data-from-slug?replayUrlSlug=${replayUrlSlug}`
    }

    static apiReplayDataFromGameId(gameId: string, playerColor: PlayerColor): string {
        return `/api/replay/data-from-game-id?gameId=${gameId}&playerColor=${playerColor}`
    }

    static apiRedeemGiftFromHash(): string {
        return `/api/gift-membership/redeem`
    }

    static apiGetGiftMembershipData(hash: string): string {
        return `/api/gift-membership/hash?hash=${hash}`
    }

    static apiSaveGiftMetadata(): string {
        return '/api/gift-membership/metadata'
    }

    static apiReactivationMessage(): string {
        return '/api/plan-user-reactivation'
    }

    static apiChampionship25Content(): string {
        return '/api/championship25/content'
    }

    static apiFreePlayDiceRoll(): string {
        return '/api/free-play-dice-roll'
    }
}
